import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import {
  getIntelligenceInspectors,
  getIntelligenceInspectorDetail,
  type InspectorRow,
  type InspectorDetailResponse,
} from '../lib/api';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { ApiErrorNotice } from '../components/ApiErrorNotice';
import { apiErrorFromResponse, shouldRetryApiError } from '../lib/api-errors';

// ── Forbidden-word check (mirrors CT-128, client-side safety net) ─────────────
// The server enforces this; the UI checks before rendering as a defence-in-depth.
const FORBIDDEN_PATTERNS: readonly RegExp[] = [
  /\bstrict\b/i, /\blenient\b/i, /\bharsh\b/i, /\btough\b/i,
  /\beffective\b/i, /\bineffective\b/i, /\bgood\b/i, /\bbad\b/i,
  /\btop[-]?performing\b/i, /\bunder[-]?performing\b/i,
];

function hasForbiddenWord(text: string): boolean {
  return FORBIDDEN_PATTERNS.some((p) => p.test(text));
}

function sanitizeField(value: unknown): string {
  const s = String(value ?? '');
  if (hasForbiddenWord(s)) return '[vocabulary-gated]';
  return s;
}

// ── Inspector Detail Panel ────────────────────────────────────────────────────

interface DetailPanelProps {
  readonly inspectorId: string;
  readonly token: string;
  readonly onClose: () => void;
}

function InspectorDetailPanel({ inspectorId, token, onClose }: DetailPanelProps): React.ReactElement {
  const { data, error } = useSWR<InspectorDetailResponse>(
    ['intelligence-inspector-detail', inspectorId],
    async () => {
      const res = await getIntelligenceInspectorDetail(token, inspectorId);
      const body = (await res.json().catch(() => null)) as InspectorDetailResponse | { error: string } | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Failed to load inspector.');
      }
      return body as InspectorDetailResponse;
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  const inspector = data?.inspector;
  const profileAvailable = inspector?.profile_available === true;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" role="dialog" aria-modal="true" aria-label="Inspector detail">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 truncate pr-4">
            {inspector ? sanitizeField(inspector.canonical_name) : 'Loading…'}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 text-xl leading-none ml-2"
            aria-label="Close inspector panel"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {error ? (
            <ApiErrorNotice error={error} fallbackMessage="Failed to load inspector." />
          ) : !data ? (
            <div className="text-slate-500 text-sm">Loading inspector profile…</div>
          ) : !profileAvailable ? (
            // CT-125: ineligible inspector → explicit empty state, never partial data
            <div
              className="rounded-md bg-slate-50 border border-slate-200 p-5"
              data-testid="ineligible-inspector-state"
            >
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Inspector profile not exposure-eligible
              </p>
              <p className="text-sm text-slate-600 mb-4">
                {inspector?.reason ?? 'This inspector profile does not meet the minimum data thresholds for public exposure.'}
              </p>
              {inspector?.correction_channel && (
                <p className="text-xs text-slate-500">{String(inspector.correction_channel)}</p>
              )}
              {/* Pattern inference warning always shown */}
              {(inspector?.warnings ?? []).map((w: { code: string; message: string }, i: number) => (
                <div key={i} className="mt-3 rounded bg-amber-50 border border-amber-100 px-3 py-2">
                  <p className="text-xs text-amber-800 font-medium">{w.code}</p>
                  <p className="text-xs text-amber-700 mt-0.5">{w.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Pattern inference warning — always shown before data (CT-127) */}
              {(inspector.warnings ?? []).map((w: { code: string; message: string }, i: number) => (
                <div
                  key={i}
                  className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 mb-4 text-xs"
                  data-testid="pattern-warning"
                >
                  <p className="font-medium text-amber-800">{w.code}</p>
                  <p className="text-amber-700 mt-0.5">{w.message}</p>
                </div>
              ))}

              {/* Profile data */}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-6">
                <dt className="text-slate-500">Name</dt>
                <dd className="text-slate-800 font-medium">{sanitizeField(inspector.canonical_name)}</dd>

                <dt className="text-slate-500">Office</dt>
                <dd className="text-slate-800">{inspector.primary_office ? sanitizeField(inspector.primary_office) : '—'}</dd>

                <dt className="text-slate-500">Linked inspections</dt>
                <dd className="text-slate-800 tabular-nums" data-testid="inspector-linked-inspections">
                  {String(inspector.total_linked_inspections ?? '—')}
                </dd>

                <dt className="text-slate-500">First seen</dt>
                <dd className="text-slate-800">{String(inspector.first_seen_at ?? '—')}</dd>

                <dt className="text-slate-500">Last seen</dt>
                <dd className="text-slate-800">{String(inspector.last_seen_at ?? '—')}</dd>

                <dt className="text-slate-500">Active years</dt>
                <dd className="text-slate-800">{String(inspector.active_years ?? '—')}</dd>
              </dl>

              {/* Signals */}
              {inspector.signals && inspector.signals.length > 0 && (
                <section className="mb-5">
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
                    Profile Signals
                  </h4>
                  <ul className="space-y-2">
                    {inspector.signals.slice(0, 20).map((s: Record<string, unknown>, i: number) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <span className="text-xs rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">
                          {sanitizeField(s.signal_type)}
                        </span>
                        <span className="text-slate-700">{sanitizeField(s.dimension)}</span>
                        {typeof s.z_score === 'number' && (
                          <span className="ml-auto text-xs text-slate-400 tabular-nums">
                            z={s.z_score.toFixed(2)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Correction channel */}
              {inspector.correction_channel && (
                <p className="text-xs text-slate-400 border-t pt-3">
                  {String(inspector.correction_channel)}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Inspectors Page ───────────────────────────────────────────────────────────

export function IntelligenceInspectorsPage(): React.ReactElement {
  const { user, session } = useAuth();

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const cacheKey = user && session ? ['intelligence-inspectors', user.id, query] : null;

  const { data, error } = useSWR<{ inspectors: InspectorRow[] }>(
    cacheKey,
    async () => {
      const res = await getIntelligenceInspectors(session!.access_token, { query });
      const body = (await res.json().catch(() => null)) as { inspectors: InspectorRow[] } | { error: string } | null;
      if (!res.ok) {
        throw apiErrorFromResponse(res.status, body, 'Failed to load inspectors.');
      }
      return body as { inspectors: InspectorRow[] };
    },
    { shouldRetryOnError: shouldRetryApiError },
  );

  const inspectors = data?.inspectors ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Intelligence: Inspectors</h2>
        <Link
          to="/intelligence/graph"
          className="text-sm font-medium text-sentinel-700 hover:text-sentinel-900"
        >
          ← Evidence Graph
        </Link>
      </div>

      <p className="text-sm text-slate-500 mb-4">
        Inspector dossier view. Only exposure-eligible inspectors are shown. Pattern signals are
        historical inferences — not outcome predictions or assessments of performance.
      </p>

      {/* Ethics disclosure */}
      <div className="rounded-md bg-slate-50 border border-slate-200 p-4 mb-6 text-xs text-slate-600">
        <span className="font-semibold">Ethics note:</span> Inspector data is exposure-gated.
        Ineligible profiles are never shown. Pattern signals must not be used to predict inspection
        outcomes or compromise inspection integrity.
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="inspector-query">
          Search inspectors
        </label>
        <input
          id="inspector-query"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Inspector name…"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sentinel-500"
        />
      </div>

      {error ? (
        <ApiErrorNotice error={error} fallbackMessage="Failed to load inspectors." />
      ) : !data ? (
        <div className="text-slate-500 text-sm">Loading inspectors…</div>
      ) : inspectors.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-slate-400 text-sm">
          {query
            ? 'No eligible inspectors found matching your search.'
            : 'No exposure-eligible inspectors found in the database yet.'}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Name</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Office</th>
                <th className="text-right px-4 py-3 text-slate-600 font-medium">Inspections</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">First seen</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {inspectors.map((inspector) => (
                <tr
                  key={inspector.inspector_id}
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedId(inspector.inspector_id)}
                >
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    {sanitizeField(inspector.canonical_name)}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {inspector.primary_office ? sanitizeField(inspector.primary_office) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                    {String(inspector.total_linked_inspections ?? '—')}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{String(inspector.first_seen_at ?? '—')}</td>
                  <td className="px-4 py-2.5 text-slate-500">{String(inspector.last_seen_at ?? '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && session && (
        <InspectorDetailPanel
          inspectorId={selectedId}
          token={session.access_token}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
